import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
  Status as StatusInterface,
  ModalFilter,
  MultiMetricData,
  SlicerDimensions,
  SlicerSettings,
  GraphLayoutProps,
  IdNameObject
} from '../../../types';
import { ReduxStates } from '../../../redux/reduxTypes';
import { returnParams, fetchSlicerDimension, fetchSlicer, parseMultiMetricData, parseExportData } from '../helpers';
import Graph from './Graph/Graph';
import SlicerMenu from './SlicerMenu';
import Export from '../../../components/Export';
import { demoScratchpadSlicer } from '../../../helpers/demoValues';
import moment from 'moment';
import { getDates } from '../../../helpers/functions';
import { useOktaAuth } from '@okta/okta-react';

interface Props {
  marketFilters: (ModalFilter | null)[];
  formattedDates: string;
  selectedCurrency: string | null;
}

const Slicer = ({ marketFilters, formattedDates, selectedCurrency }: Props): React.ReactElement => {
  const [metrics, setMetrics] = useState<Array<MultiMetricData> | null>(null);
  const [status, setStatus] = useState<StatusInterface[]>([]);
  const [fetching, setFetching] = useState<boolean>(true);
  const [noData, setNoData] = useState<boolean>(true);
  const [fetchingGraph, setFetchingGraph] = useState<boolean>(true);
  const [dimensions, setDimensions] = useState<Array<SlicerDimensions> | null>(null);
  const [slicerSettings, setSlicerSettings] = useState<SlicerSettings | null>(null);
  const [valueOrPercentage, setValueOrPercentage] = useState<string>('value');
  const [graphLayout, setGraphLayout] = useState<GraphLayoutProps | null>(null);
  const [showExportOptions, setShowExportOptions] = useState<boolean>(false);
  const [exportData, setExportData] = useState<Array<any> | null>(null);
  const [dateRange, setDateRange] = useState<string[] | null>(null);
  const client = useSelector((state: ReduxStates) => state.client);

  const [isPercentageValue, setIsPercentageValue] = useState<boolean>(false);
  const [retailers, setRetailers] = useState<Array<any> | undefined | null>(null);

  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const lineGraphEl: any = useRef(null);
  const parentRef: any = useRef(null);

  const { oktaAuth } = useOktaAuth();

  const defaultMetric: IdNameObject = {
    id: 'orderedRevenue',
    name: 'Amazon POS'
  };

  const fetchDimensions = async (params: any) => {
    try {
      const dimensions = await fetchSlicerDimension(params);
      setDimensions(dimensions?.dimensions);
      setFetching(false);
    } catch (error: any) {
      setStatus([...status, { state: 'error', message: error }]);
      setFetching(false);
    }
  };

  const fetchSlicerData = async (params: any, sliceBy: string, sliceByMetric: string) => {
    try {
      const data = await fetchSlicer(params, sliceBy, sliceByMetric, oktaAuth);
      if (data?.metrics[0].valuesWithDates.length) {
        const parsedData = parseMultiMetricData(data?.metrics);
        setNoData(false);
        if (!(client === 'demo' && sliceByMetric === 'spgr')) {
          setMetrics(parsedData);
        } else {
          switch (sliceBy) {
            case 'Brand':
              setMetrics(demoScratchpadSlicer[0]);
              break;
            case 'Category':
              setMetrics(demoScratchpadSlicer[1]);
              break;
            case 'Market':
              setMetrics(demoScratchpadSlicer[2]);
              break;
            case 'Region':
              setMetrics(demoScratchpadSlicer[3]);
              break;
            case 'Retailer':
              setMetrics(demoScratchpadSlicer[4]);
              break;
            default:
              setMetrics(demoScratchpadSlicer[0]);
          }
        }
      } else {
        setNoData(true);
      }
      setFetchingGraph(false);
    } catch (error: any) {
      setStatus([...status, { state: 'error', message: error }]);
      setFetchingGraph(false);
    }
  };

  useEffect(() => {
    if (!retailers) {
      setRetailers(marketFilters.find((filter) => filter?.name === 'Retailer')?.values?.map((item) => item.name));
    } else {
      const newRetailers = marketFilters
        .find((filter) => filter?.name === 'Retailer')
        ?.values?.map((item) => item.name);
      const compare = JSON.stringify(retailers) === JSON.stringify(newRetailers);
      if (!compare) setRetailers(newRetailers);
    }
  }, [marketFilters]);

  useEffect(() => {
    if (formattedDates && selectedCurrency && marketFilters.length && retailers) {
      const params = returnParams(marketFilters, formattedDates);
      setFetching(true);
      fetchDimensions(params);
    }
  }, [retailers]);

  useEffect(() => {
    if (formattedDates) {
      resetSlicerData();
      if (selectedCurrency && marketFilters.length) {
        const params = returnParams(marketFilters, formattedDates);

        if (dimensions && slicerSettings) {
          setFetchingGraph(true);
          fetchSlicerData(params, slicerSettings.sliceBy, slicerSettings.metric.id);
        }
      }
      const dateFilters = formattedDates.split(' - ');
      const startDate = moment(new Date(dateFilters[0])).format('YYYY-MM-DD');
      const endDate = moment(new Date(dateFilters[1])).format('YYYY-MM-DD');
      setDateRange(getDates(startDate, endDate));
    }
  }, [formattedDates, marketFilters, selectedCurrency]);

  useEffect(() => {
    if (metrics && metrics.length) {
      setIsPercentageValue(metrics[0].currentValueType === 'percentage');
    }
  }, [metrics]);

  const resetSlicerData = () => {
    setMetrics(null);
  };

  useEffect(() => {
    if (slicerSettings && formattedDates && marketFilters) {
      setMetrics(null);
      setFetchingGraph(true);
      const params = returnParams(marketFilters, formattedDates);
      fetchSlicerData(params, slicerSettings.sliceBy, slicerSettings.metric.id);
    }
  }, [slicerSettings]);

  const updateSettings = (options: SlicerSettings) => {
    const sliceBy = options.sliceBy;
    let metric: IdNameObject = options.metric;
    if (sliceBy === slicerSettings?.sliceBy && metric.id === slicerSettings.metric.id) return;
    if (sliceBy !== slicerSettings?.sliceBy) {
      const dimensionMetrics = dimensions?.find((d) => d.name === sliceBy);
      const isInMetrics = dimensionMetrics?.metrics.find((m: IdNameObject) => m.id === metric.id);
      if (!isInMetrics && dimensionMetrics) {
        metric = defaultMetric;
      }
    }
    setSlicerSettings({ sliceBy, metric });
  };

  useEffect(() => {
    if (metrics && metrics.length && selectedCurrency) {
      setExportData(parseExportData(metrics, selectedCurrency, valueOrPercentage));
    }
  }, [metrics, valueOrPercentage]);

  const getFileName = () => {
    const metricString = slicerSettings?.metric?.name.replace(/ /g, '').toLowerCase();
    const fileName = slicerSettings?.sliceBy.toLowerCase() + '_' + metricString;
    return fileName;
  };

  const updateGraphVisibility = (visible: boolean, id: string) => {
    if (metrics) {
      const newArr = [...metrics]; // copying the old datas array
      const dataDimension = newArr.find((dimension) => dimension?.dimension === id);
      if (dataDimension) dataDimension.graphVisible = visible;
      setMetrics(newArr);
    }
  };

  return (
    <div className="bg-white" ref={parentRef}>
      <div className="relative flex justify-end pt-[18px] pr-[24px]">
        {metrics && (
          <Export
            graph={parentRef}
            data={exportData}
            fileName={getFileName()}
            showExportOptions={showExportOptions}
            setShowExportOptions={setShowExportOptions}
            automationID="slicer_export_button"
          />
        )}
      </div>
      {dimensions && (
        <SlicerMenu
          dimensions={dimensions}
          onUpdateSettings={(options: SlicerSettings) => updateSettings(options)}
          onUpdateValuePercentage={(valuePercentage: string) => setValueOrPercentage(valuePercentage)}
          onUpdateGraphLayout={(graphLayout: GraphLayoutProps) => setGraphLayout(graphLayout)}
          isPercentageValue={isPercentageValue}
          defaultMetric={
            retailers?.length === 1 && retailers[0] === 'Amazon' ? defaultMetric : dimensions[0].metrics[0]
          }
          disableMenu={fetchingGraph}
        />
      )}

      <div id="slicer_wrapper" className="mt-[54px]">
        {fetching || fetchingGraph ? (
          <div className="w-full h-full min-h-[200px] flex items-center justify-center">
            <p className="font-nunitoRegular text-filterList text-base">{copyData?.loading}</p>
          </div>
        ) : metrics && !noData ? (
          <Graph
            graphData={metrics}
            dateRange={dateRange}
            lineGraphEl={lineGraphEl}
            selectedCurrency={selectedCurrency}
            valueOrPercentage={valueOrPercentage}
            graphLayout={graphLayout}
            updateGraphVisibility={updateGraphVisibility}
          />
        ) : (
          <div className="w-full h-full min-h-[200px] flex items-center justify-center">
            <p className="font-nunitoBold text-filterList text-base">{copyData?.no_data}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Slicer;
